"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import StatsSection from "./components/StatsSection";
import WhatWeRepair from "./components/WhatWeRepair";
import ProcessSection from "./components/ProcessSection";
import CommonIssues from "./components/CommonIssues";
import NeighborhoodCoverage from "./components/NeighborhoodCoverage";
import ScopeSection from "./components/ScopeSection";
import FaqSection from "@/components/feature/FaqSection";
import ServiceAreasGrid from "@/components/feature/ServiceAreasGrid";
import RelatedServicesBlockService from "@/components/feature/RelatedServicesBlockService";
import BottomCta from "./components/BottomCta";
import LeadFormSection from "@/components/feature/LeadFormSection";
import AboutServiceSection from "@/components/feature/AboutServiceSection";
import { injectMetaTags } from "@/lib/seoHelpers";

const neighborhoods = [
  "Brickell",
  "Aventura",
  "Miami Beach",
  "Coral Gables",
  "Coconut Grove",
  "Doral",
  "Kendall",
  "Pinecrest",
  "Bay Harbor Islands",
  "Key Biscayne",
  "South Miami",
  "North Miami Beach",
];

export default function WindowHardwareRepairPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const hardwareAboutImg =
    "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/1357dca9-e496-4c9b-aa80-50afa22b0119_windows-hardware-miami.jpg?v=a49edce5c95b8c245efa1f0cad36005a";

  const hardwareFaqs = [
    {
      question: t("hardware.faq1Q") as string,
      answer: t("hardware.faq1A") as string,
    },
    {
      question: t("hardware.faq2Q") as string,
      answer: t("hardware.faq2A") as string,
    },
    {
      question: t("hardware.faq3Q") as string,
      answer: t("hardware.faq3A") as string,
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Reparación de Herrajes de Ventanas Miami — Cerraduras, Manivelas"
      : "Window Hardware Repair Miami — Locks, Cranks, Hinges";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      currentLang === "es"
        ? "Reparación de herrajes de ventanas en Miami: cerraduras, manivelas, bisagras, rodillos, balances. Reemplazos de acero inoxidable y marina para aire salino costero. Servicio el mismo día."
        : "Miami window hardware repair: locks, cranks, hinges, rollers, balances. Stainless and marine-grade replacements for coastal salt air. Same-day service."
    );

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es" ? "Reparación de Herrajes de Ventanas Miami | Cerraduras, Bisagras, Rodillos" : "Window Hardware Repair Miami | Locks, Hinges, Cranks, Rollers",
      description: currentLang === "es" ? "Reparación de herrajes de ventanas en Miami. Reemplazo de rodillos, cerraduras, bisagras, manivelas. Servicio el mismo día en 12 vecindarios de Miami-Dade." : "Window hardware repair Miami. Replace worn rollers, locks, hinges, cranks, handles. Same-day across 12 Miami-Dade neighborhoods.",
      url: "https://impact-window-repair-miami.com/window-hardware-repair/",
      image: "https://impact-window-repair-miami.com/og/og-hardware.jpg",
      imageAlt: "Window hardware repair Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "Reparación de Herrajes" : "Window Hardware Repair", item: "https://impact-window-repair-miami.com/window-hardware-repair/" }
      ],
      type: "website",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Window Hardware Repair",
      provider: {
        "@type": "LocalBusiness",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        email: "contact@impact-window-repair-miami.com",
        url: "https://impact-window-repair-miami.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          "latitude": 25.7617,
          "longitude": -80.1918
        },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "07:00",
          "closes": "21:00"
        }],
        priceRange: "$$",
        paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
        currenciesAccepted: "USD",
        areaServed: [
          {"@type": "City", "name": "Miami"},
          {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
          "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
        ],
        knowsLanguage: ["en", "es"],
        knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
        slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
        inLanguage: currentLang === "es" ? "es-US" : "en-US"
      },
      serviceType: "Window and Door Hardware Repair",
      description:
        "Repair and replacement of window and door hardware including rollers, locks, handles, cranks, hinges, and tracks across Miami-Dade County.",
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: hardwareFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const faqScript = document.createElement("script");
    faqScript.setAttribute("type", "application/ld+json");
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, [currentLang]);

  return (
    <div className="font-roboto">
      <HeaderStrip />
      <HeroSection />
      <LeadFormSection defaultService="hardware" />
      <AboutServiceSection
        badge={t("hardware.eyebrow") as string}
        imageSrc={hardwareAboutImg}
        imageAlt={t("hardware.aboutImageAlt") as string}
        heading={t("hardware.aboutHeading") as string}
        lead={t("hardware.intro") as string}
        bullets={[
          t("hardware.aboutBullet1") as string,
          t("hardware.aboutBullet2") as string,
          t("hardware.aboutBullet3") as string,
          t("hardware.aboutBullet4") as string,
          t("hardware.aboutBullet5") as string,
        ]}
        closing={t("hardware.aboutClosing") as string}
      />
      <StatsSection />
      <WhatWeRepair />
      <ProcessSection />
      <CommonIssues />
      <NeighborhoodCoverage />
      <ScopeSection />
      <FaqSection items={hardwareFaqs} heading={t("hardware.faqH2") as string} />
      <ServiceAreasGrid serviceName="Window Hardware Repair" serviceSlug="window-hardware-repair" />
      <RelatedServicesBlockService
        links={[
          { label: t("hardware.related1") as string, path: "/sliding-door-repair/" },
          { label: t("hardware.related2") as string, path: "/weatherstripping-sealing/" },
          { label: t("hardware.related3") as string, path: "/emergency-board-up/" },
        ]}
      />
      <BottomCta />
    </div>
  );
}